import { useMemo, useState } from "react";
import {
  acknowledgeBoardNoStart,
  addManualNoStart,
  captureIdea,
  evaluateAndSaveFocusActivation,
  type FocusOverview,
} from "../application/round03-service";
import type { BusinessContextView } from "../application/round02-service";
import type { CEOOSDatabase } from "../db/database";
import type { CEOProfile, IdeaParkingItem } from "../domain/types";
import { safeErrorMessage } from "../privacy/policy";
import type { FocusActivationAnswers } from "../rules/focus-activation";
import { GovernanceTabs, type GovernanceArea } from "../ui/GovernanceTabs";

const EMPTY_ANSWERS: FocusActivationAnswers = {
  servesMainline: null,
  solvesConstraint: null,
  supportingEvidence: "",
  resourceRequirement: "",
  opportunityCost: "",
  experimentType: "unknown",
  stopCondition: "",
  needsBoardApproval: null,
};

const RESULT_LABELS = {
  remain_parked: "继续停放",
  reject: "拒绝启动",
  small_reversible_experiment: "仅允许小型可逆实验",
  escalate_to_board: "升级至董事会 OS",
} as const;

function BinaryChoice({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
}) {
  return (
    <fieldset className="governance-choice">
      <legend>{label}</legend>
      <div>
        <button
          type="button"
          className={value === true ? "is-selected" : ""}
          aria-pressed={value === true}
          onClick={() => onChange(true)}
        >
          是
        </button>
        <button
          type="button"
          className={value === false ? "is-selected" : ""}
          aria-pressed={value === false}
          onClick={() => onChange(false)}
        >
          否
        </button>
      </div>
    </fieldset>
  );
}

function IdeaReview({
  database,
  idea,
  onClose,
  onSaved,
}: {
  database: CEOOSDatabase;
  idea: IdeaParkingItem;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [answers, setAnswers] = useState<FocusActivationAnswers>(
    idea.activationReview ? { ...idea.activationReview } : EMPTY_ANSWERS,
  );
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [openedAtMs] = useState(() => Date.now());
  const coolingActive = new Date(idea.coolingUntil).getTime() > openedAtMs;

  const submit = async () => {
    setSaving(true);
    try {
      const result = await evaluateAndSaveFocusActivation(database, idea.id, answers);
      setNotice(
        `${RESULT_LABELS[result.decision.result]}。${result.decision.explanation}${
          result.decision.issues.length
            ? ` ${result.decision.issues.map((item) => item.message).join("；")}`
            : ""
        }`,
      );
      await onSaved();
    } catch (error) {
      setNotice(safeErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="governance-page">
      <header className="governance-header">
        <button type="button" className="text-button" onClick={onClose}>
          ← 返回聚焦守卫
        </button>
        <span className="eyebrow">Focus Activation Gate</span>
        <h1>{idea.title}</h1>
        <p>
          捕获于 {new Date(idea.capturedAt).toLocaleString("zh-CN")} · 冷静期至{" "}
          {new Date(idea.coolingUntil).toLocaleString("zh-CN")}
        </p>
      </header>

      {coolingActive && (
        <div className="risk-rail" role="status">
          <strong>CEO-FOCUS-001 · 冷静期仍在进行</strong>
          <p>可以填写审查，但结果只能保持停放。系统不会把热情升级成项目批准。</p>
        </div>
      )}
      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}

      <section className="governance-form" aria-labelledby="activation-questions">
        <h2 id="activation-questions">八项启动审查</h2>
        <BinaryChoice
          label="1. 直接服务当前 180 天主线？"
          value={answers.servesMainline}
          onChange={(value) => setAnswers({ ...answers, servesMainline: value })}
        />
        <BinaryChoice
          label="2. 直接解决当前唯一瓶颈？"
          value={answers.solvesConstraint}
          onChange={(value) => setAnswers({ ...answers, solvesConstraint: value })}
        />
        <label className="field">
          <span>3. 支持它的真实证据</span>
          <textarea
            rows={3}
            value={answers.supportingEvidence}
            onChange={(event) => setAnswers({ ...answers, supportingEvidence: event.target.value })}
          />
        </label>
        <label className="field">
          <span>4. 时间、资金与注意力成本</span>
          <textarea
            rows={3}
            value={answers.resourceRequirement}
            onChange={(event) =>
              setAnswers({ ...answers, resourceRequirement: event.target.value })
            }
          />
        </label>
        <label className="field">
          <span>5. 为此必须停止或推迟什么</span>
          <textarea
            rows={3}
            value={answers.opportunityCost}
            onChange={(event) => setAnswers({ ...answers, opportunityCost: event.target.value })}
          />
        </label>
        <label className="field">
          <span>6. 这是哪种动作</span>
          <select
            value={answers.experimentType}
            onChange={(event) =>
              setAnswers({
                ...answers,
                experimentType: event.target.value as FocusActivationAnswers["experimentType"],
              })
            }
          >
            <option value="unknown">尚未判断</option>
            <option value="reversible_experiment">小型可逆实验</option>
            <option value="formal_business">正式新业务 / 新项目</option>
          </select>
        </label>
        <label className="field">
          <span>7. 明确停止条件</span>
          <textarea
            rows={3}
            value={answers.stopCondition}
            onChange={(event) => setAnswers({ ...answers, stopCondition: event.target.value })}
          />
        </label>
        <BinaryChoice
          label="8. 是否需要董事会批准？"
          value={answers.needsBoardApproval}
          onChange={(value) => setAnswers({ ...answers, needsBoardApproval: value })}
        />
        <div className="decision-boundary">
          CEO OS 只能允许小型可逆实验、拒绝、继续停放或生成董事会升级草稿；不能批准战略项目。
        </div>
        <button
          type="button"
          className="primary-button full-width"
          disabled={saving}
          onClick={() => void submit()}
        >
          {saving ? "审查中…" : "运行 Focus Activation Gate"}
        </button>
      </section>
    </main>
  );
}

export function FocusGuard({
  database,
  overview,
  profile,
  context,
  dateKey,
  onAreaChange,
  onRefresh,
}: {
  database: CEOOSDatabase;
  overview: FocusOverview;
  profile: CEOProfile;
  context?: BusinessContextView | undefined;
  dateKey: string;
  onAreaChange: (area: GovernanceArea) => void;
  onRefresh: () => Promise<void>;
}) {
  const [activeIdeaId, setActiveIdeaId] = useState<string>();
  const [ideaTitle, setIdeaTitle] = useState("");
  const [ideaDescription, setIdeaDescription] = useState("");
  const [noStartTitle, setNoStartTitle] = useState("");
  const [noStartReason, setNoStartReason] = useState("");
  const [notice, setNotice] = useState("");
  const activeIdea = useMemo(
    () => overview.ideas.find((idea) => idea.id === activeIdeaId),
    [activeIdeaId, overview.ideas],
  );

  if (activeIdea) {
    return (
      <IdeaReview
        database={database}
        idea={activeIdea}
        onClose={() => setActiveIdeaId(undefined)}
        onSaved={onRefresh}
      />
    );
  }

  const addIdea = async () => {
    try {
      await captureIdea(database, {
        title: ideaTitle,
        description: ideaDescription,
        effectiveDate: dateKey,
        coolingHours: profile.ideaCoolingHours,
        ...(context?.current180DayMainline
          ? { relatedMainline: context.current180DayMainline }
          : {}),
        ...(context?.currentConstraint ? { relatedConstraint: context.currentConstraint } : {}),
      });
      setIdeaTitle("");
      setIdeaDescription("");
      setNotice(`想法已停放 ${profile.ideaCoolingHours} 小时；未创建项目或任务。`);
      await onRefresh();
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  const addNoStart = async () => {
    try {
      await addManualNoStart(database, {
        title: noStartTitle,
        reason: noStartReason,
        effectiveDate: dateKey,
      });
      setNoStartTitle("");
      setNoStartReason("");
      setNotice("手动 No-Start 已保存。");
      await onRefresh();
    } catch (error) {
      setNotice(safeErrorMessage(error));
    }
  };

  return (
    <main className="governance-page">
      <header className="governance-header">
        <span className="brand">CEO OS</span>
        <h1>聚焦守卫</h1>
        <p>先停放，再付机会成本。CEO OS 不把新想法自动升级成项目。</p>
      </header>
      <GovernanceTabs area="focus" onChange={onAreaChange} />
      {notice && (
        <div className="inline-notice" role="status">
          {notice}
        </div>
      )}

      <section className="governance-mast" aria-labelledby="focus-mainline">
        <span className="eyebrow">当前承诺边界</span>
        <h2 id="focus-mainline">{context?.current180DayMainline || "尚未导入 180 天主线"}</h2>
        <p>唯一瓶颈：{context?.currentConstraint || "尚未定义"}</p>
        <strong>{overview.parkedCount} 个想法正在停放</strong>
      </section>

      {overview.topRisk && (
        <section className="risk-rail">
          <span className="eyebrow">最高聚焦风险</span>
          <p>
            {"deviationContent" in overview.topRisk
              ? overview.topRisk.deviationContent
              : `${overview.topRisk.title} 正在等待启动审查。`}
          </p>
        </section>
      )}

      <details className="governance-disclosure" open>
        <summary>快速停放一个想法</summary>
        <div className="governance-form">
          <label className="field">
            <span>想法标题</span>
            <input value={ideaTitle} onChange={(event) => setIdeaTitle(event.target.value)} />
          </label>
          <label className="field">
            <span>描述（可选）</span>
            <textarea
              rows={2}
              value={ideaDescription}
              onChange={(event) => setIdeaDescription(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="primary-button"
            disabled={!ideaTitle.trim()}
            onClick={() => void addIdea()}
          >
            停放，不启动
          </button>
        </div>
      </details>

      <section className="governance-list" aria-labelledby="idea-parking-list">
        <div className="section-title-row">
          <h2 id="idea-parking-list">Idea Parking Lot</h2>
          <span>{overview.ideas.length}</span>
        </div>
        {overview.ideas.length === 0 ? (
          <div className="empty-state">
            <strong>没有停放想法</strong>
            <p>新想法会先进入冷静期，不会污染今日行动。</p>
          </div>
        ) : (
          overview.ideas.map((idea) => (
            <button
              type="button"
              className="governance-row"
              key={idea.id}
              onClick={() => setActiveIdeaId(idea.id)}
            >
              <span>
                <strong>{idea.title}</strong>
                <small>
                  {idea.parkingStatus} · 冷静期至{" "}
                  {new Date(idea.coolingUntil).toLocaleString("zh-CN")}
                </small>
              </span>
              <span aria-hidden="true">›</span>
            </button>
          ))
        )}
      </section>

      <section className="governance-list" aria-labelledby="no-start-list">
        <div className="section-title-row">
          <h2 id="no-start-list">No-Start List</h2>
          <span>{(context?.noStartList.length ?? 0) + overview.noStartItems.length}</span>
        </div>
        {context?.noStartList.map((title) => {
          const saved = overview.noStartItems.find(
            (item) =>
              item.origin === "board_import" &&
              item.sourceContextId === context.sourceId &&
              item.title === title,
          );
          return (
            <div
              className="governance-row governance-row--static"
              key={`${context.sourceId}-${title}`}
            >
              <span>
                <strong>{title}</strong>
                <small>董事会只读项 · {saved?.readAt ? "已读" : "未读"}</small>
              </span>
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  void acknowledgeBoardNoStart(database, context, title, dateKey, false)
                    .then(onRefresh)
                    .catch((error) => setNotice(safeErrorMessage(error)));
                }}
              >
                标记已读
              </button>
              <button
                type="button"
                className="text-button"
                onClick={() => {
                  void acknowledgeBoardNoStart(database, context, title, dateKey, true)
                    .then(onRefresh)
                    .catch((error) => setNotice(safeErrorMessage(error)));
                }}
              >
                请求复核
              </button>
            </div>
          );
        })}
        {overview.noStartItems
          .filter((item) => item.origin === "manual_entry")
          .map((item) => (
            <div className="governance-row governance-row--static" key={item.id}>
              <span>
                <strong>{item.title}</strong>
                <small>手动项 · {item.reason}</small>
              </span>
            </div>
          ))}
        <details className="inline-create">
          <summary>添加手动 No-Start</summary>
          <div className="governance-form">
            <label className="field">
              <span>不启动什么</span>
              <input
                value={noStartTitle}
                onChange={(event) => setNoStartTitle(event.target.value)}
              />
            </label>
            <label className="field">
              <span>原因</span>
              <input
                value={noStartReason}
                onChange={(event) => setNoStartReason(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="secondary-button"
              disabled={!noStartTitle.trim() || !noStartReason.trim()}
              onClick={() => void addNoStart()}
            >
              保存
            </button>
          </div>
        </details>
      </section>

      {overview.deviations.length > 0 && (
        <section className="governance-list" aria-labelledby="focus-deviations">
          <div className="section-title-row">
            <h2 id="focus-deviations">复盘识别的聚焦偏差</h2>
            <span>{overview.deviations.length}</span>
          </div>
          {overview.deviations.map((deviation) => (
            <div className="governance-row governance-row--static" key={deviation.id}>
              <span>
                <strong>{deviation.deviationContent}</strong>
                <small>明日纠偏：{deviation.tomorrowCorrection}</small>
              </span>
            </div>
          ))}
        </section>
      )}
    </main>
  );
}
